import { rest } from "msw";
import type { DomainConfig } from "../config.ts";
import type { UsersAdapterResourceServiceType } from "../adapters/UsersAdapter.ts";
import {
  LegacyUserClaimSchema,
  type LegacyUserClaimType,
  UserSchema,
  UserStatus,
  UserTypeEnum,
  type UserType,
} from "../schemas/UserSchemas.ts";
import {
  getApiUrlWithEndpoint,
  type BuildMockHandlersType,
} from "./handlers.ts";
import { DomainFaker, DomainMock } from "./utils.ts";

export const legacyUserClaimMock = new DomainMock<
  LegacyUserClaimType,
  typeof LegacyUserClaimSchema
>(
  LegacyUserClaimSchema,
  () => ({
    id: DomainFaker.en.string.uuid(),
    name: DomainFaker.en.person.fullName(),
    email: DomainFaker.en.internet.email(),
    site_ids: [],
  }),
  true
);

export const userMock = new DomainMock<UserType, typeof UserSchema>(
  UserSchema,
  () => {
    const createdAt = DomainFaker.en.date.past();

    return {
      id: DomainFaker.en.string.uuid(),
      name: DomainFaker.en.person.fullName(),
      email: DomainFaker.en.internet.email(),
      type: UserTypeEnum.human,
      quicksight: DomainFaker.en.helpers.arrayElement([true, false]),
      can_access_quicksight_dashboards: DomainFaker.en.helpers.arrayElement([
        true,
        false,
      ]),
      can_access_quicksight_console: DomainFaker.en.helpers.arrayElement([
        true,
        false,
      ]),
      phone: DomainFaker.en.phone.number(),
      status: DomainFaker.en.helpers.arrayElement(Object.values(UserStatus)),
      auth0_id: DomainFaker.en.string.uuid(),
      timezone: DomainFaker.en.location.timeZone(),
      created_at: createdAt.toISOString(),
      created_by: DomainFaker.en.string.uuid(),
      updated_at: DomainFaker.en.date
        .between({ from: createdAt, to: new Date() })
        .toISOString(),
      updated_by: DomainFaker.en.string.uuid(),
      roles: [
        {
          id: DomainFaker.en.string.uuid(),
          name: DomainFaker.en.lorem.words({ min: 1, max: 3 }),
        },
      ],
      roles_count: 1,
      user_groups: [
        {
          id: DomainFaker.en.string.uuid(),
          name: DomainFaker.en.lorem.words({ min: 1, max: 3 }),
        },
        {
          id: DomainFaker.en.string.uuid(),
          name: DomainFaker.en.lorem.words({ min: 1, max: 3 }),
        },
      ],
      user_groups_count: 2,
      company_membership: [],
    };
  },
  true
).fix();

export const buildUserHandlers: BuildMockHandlersType<
  UsersAdapterResourceServiceType
> = (configData: DomainConfig) => {
  const { nodeURL: url } = configData;

  return {
    getList: rest.post(
      getApiUrlWithEndpoint({
        url,
        version: 1,
        endpoint: "/users/search",
      }),
      (req, res, ctx) => {
        const page = Number(req.url.searchParams.get("page"));
        const pageSize = Number(req.url.searchParams.get("pageSize"));
        const data = new Array(30).fill(0).map(() => userMock.generate());

        return res(
          ctx.status(200),
          ctx.json({
            data,
            page_number: page,
            page_size: pageSize,
            total_entries: data.length,
            total_pages: 1,
          })
        );
      }
    ),
    getOne: rest.get(
      getApiUrlWithEndpoint({
        url,
        version: 1,
        endpoint: "/users/:id",
      }),
      (req, res, ctx) => {
        const { id } = req.params;

        if (id === "myself") return req.passthrough();

        const mockData = userMock.mock;

        if (typeof id !== "string") {
          return res(ctx.status(400));
        }

        mockData.id = id;

        return res(ctx.status(200), ctx.json(mockData));
      }
    ),
  };
};
